import type { BaseObject, BaseProperty, RecordMode, TableFilterData } from './Base';
import type { Company, CompanySite } from './Companies';
import type { AlterCode, Material, MeasurementUnit } from './Materials';
import type { Customer, Representative, TraderSite } from './Traders';
import type { DocumentType } from './DocumentTypes';
import type { User } from './Users';
import type { PaymentType } from './PaymentType';

export enum eIOMethodType {
  Csv = 1,
  FixedLength = 2,
  json = 3,
  RestApi = 4,
}

export interface DCDocumentType extends BaseObject {
  documentTypeId?: string | null;
  representativeId?: string;
  documentTypeRemotedCode: string;
  toApprove: boolean;
  allowUpdate?: boolean;
  allowChange?: boolean;
  exportMethodType: eIOMethodType;
  lastExportedDate?: string;
  exportFolder?: string;
  exportUrl?: string;
  exportFileNameFormat?: string;
  documentType?: Partial<DocumentType>;
  recordMode?: RecordMode;
}

export type DCMaterialAlterCode = BaseObject & {
  materialAlterCodeId?: string;
  representativeId?: string;
  isActive?: boolean;
  isFromManager?: boolean;
  materialAlterCodeRemotedCode: string;
  materialAlterCode?: {
    id?: string;
    material?: Partial<Material>;
    alterCode?: Partial<AlterCode>;
    measurementUnitId?: string;
    materialId?: string;
    alterCodeId?: string;
    measurementUnit?: MeasurementUnit;
  };
  recordMode?: RecordMode;
};

export type DCMeasurementUnit = BaseObject & {
  measurementUnitId?: string;
  representativeId?: string;
  measurementUnitRemotedCode?: string;
  isActive?: boolean;
  isFromManager?: boolean;
  measurementUnit?: BaseProperty;
  recordMode?: RecordMode;
};

export type DCPaymentType = BaseObject & {
  paymentTypeId: string;
  representativeId: string;
  paymentMethodRemotedCode: string;
  paymentType?: BaseProperty;
  recordMode?: RecordMode;
};

export type DCWarehouse = BaseObject & {
  warehouseId: string;
  representativeId: string;
  warehouseRemotedCode: string;
  warehouse?: BaseProperty;
  recordMode?: RecordMode;
};

export type DCDocumentHeader = BaseObject & {
  documentHeaderRowId: string;
  source: string;
  companyId: string;
  company?: Partial<Company>;
  representativeCustomerCode?: string;
  representativeCustomerName?: string;
  customerSiteCode?: string;
  customerSiteName?: string;
  representativeRecipientCode?: string;
  representativeRecipientName?: string;
  recipientSiteCode?: string;
  recipientSiteName?: string;
  documentNumber?: string;
  isInvalid: boolean;
  officialDate?: string;

  companySiteId: string;
  companySite?: Partial<CompanySite>;

  representativeId: string;
  representative?: Partial<Representative>;
  representativeSiteId?: string;
  representativeSite?: Partial<TraderSite>;

  representativeCustomerTaxCode?: string;
  representativeCustomerAddress?: string;
  representativeCustomerCity?: string;
  representativeCustomerId?: string;
  representativeCustomer?: DCCustomer;
  companyCustomerId?: string;
  companyCustomer?: Partial<Customer>;

  customerSiteAddress?: string;
  customerSiteCity?: string;
  customerSiteId?: string;
  customerSite?: Partial<TraderSite>;

  representativeRecipientTaxCode?: string;
  representativeRecipientAddress?: string;
  representativeRecipientCity?: string;
  representativeRecipientId?: string;
  representativeRecipient?: DCCustomer;
  companyRecipientId?: string;
  companyRecipient?: Partial<Customer>;

  recipientSiteAddress?: string;
  recipientSiteCity?: string;
  recipientSiteId?: string;
  recipientSite?: Partial<TraderSite>;

  representativeDocumentTypeCode?: string;
  representativeDocumentTypeId?: string;
  representativeDocumentType?: DCDocumentType;
  companyDocumentTypeId?: string | null;
  companyDocumentType?: Partial<DocumentType>;

  documentDescription?: string;

  representativeDocumentSeriesCode?: string;

  representativePaymentTypeCode?: string;
  representativePaymentTypeId?: string;
  representativePaymentType?: DCPaymentType;
  companyPaymentTypeId?: string;
  companyPaymentType?: Partial<PaymentType>;

  isApproved?: boolean;
  approvalUserId?: string;
  approvalUser?: Partial<User>;

  officialDateDT?: string;
  remarks?: string;

  detailsCount: number;
  totalGrossAmount: number;
  totalNetAmount: number;
  totalVatAmount: number;
  totalQuantity: number;

  recordMode?: RecordMode;
  documentDetails?: DCDocumentDetails[];
  dcDocumentPaymentTypes?: Partial<DCPaymentType>[];
  dcMeasurementUnits?: Partial<DCMeasurementUnit>[];
  dcMaterialAlterCodes?: Partial<DCMaterialAlterCode>[];
  exported: boolean;
  userExport: boolean;
};

export type DCRepresentativeImport = BaseObject & {
  representativeId: string;
  code: string;
  name: string;
  remarks: string;
  lastImportDate?: string;
  lastImportDateString?: string;
  importFolder: string;
  importUrl: string;
  importFileNameFormat: string;
  recordMapping: string;
  recordMode?: RecordMode;
};

export type TransactionsFiltersData = TableFilterData & {
  isInvalid?: boolean;
  toApprove?: boolean;
  representativesIds?: string[];
  exported?: boolean;
  dateFrom?: string;
  dateTo?: string;
};
export type RepresentativeFiltersData = TableFilterData & {
  representativeId?: string | null;
  traderId?: string;
  isMapped?: boolean;
};

export interface DCMaterialAlterCodeDto {
  id: string;
  materialId: string;
  representativeId: string;
  alterCode: string;
  material?: any; // Will be defined when needed
  recordMode?: RecordMode;
}

export interface DCDocumentDetails extends BaseObject {
  id: string;
  dcDocumentHeaderId: string;
  documentDetailRowId: string;

  // Representative Material Alter Code
  representativeMaterialAlterCodeCode: string;
  representativeMaterialAlterCodeName?: string;
  representativeMaterialAlterCodeId?: string;
  companyMaterialAlterCodeId?: string;

  // Quantity 1
  qty1: number;
  representativeMeasurementUnitCode1?: string;
  representativeMeasurementUnit1Id?: string;
  companyMeasurementUnit1Id?: string;

  // Quantity 2
  qty2: number;
  representativeMeasurementUnitCode2?: string;
  representativeMeasurementUnit2Id?: string;
  companyMeasurementUnit2Id?: string;

  // Quantity 3
  qty3: number;
  representativeMeasurementUnitCode3?: string;
  representativeMeasurementUnit3Id?: string;
  companyMeasurementUnit3Id?: string;

  // Pricing
  unitPrice?: number;

  // Discounts (6 levels)
  discount1?: number;
  discount2?: number;
  discount3?: number;
  discount4?: number;
  discount5?: number;
  discount6?: number;

  // Discount Values
  discountValue1?: number;
  discountValue2?: number;
  discountValue3?: number;
  discountValue4?: number;
  discountValue5?: number;
  discountValue6?: number;
  totalDiscountValue?: number;

  // Calculated Values
  netValue?: number;
  vatPercent?: number;
  vatValue?: number;
  grossValue?: number;

  // Nested Objects
  companyMaterialAlterCode?: DCMaterialAlterCode;
  representativeMaterialAlterCode?: DCMaterialAlterCode;
  companyMeasurementUnit1?: MeasurementUnit;
  representativeMeasurementUnit1?: DCMeasurementUnit;
  companyMeasurementUnit2?: MeasurementUnit;
  representativeMeasurementUnit2?: DCMeasurementUnit;
  companyMeasurementUnit3?: MeasurementUnit;
  representativeMeasurementUnit3?: DCMeasurementUnit;

  isActive: boolean;
  deleted: boolean;
  createdDate: string;
  lastUpdate?: string;
  createUserId?: string;
  updateUserId?: string;
  isFromManager: boolean;
}

export type DCCustomer = BaseObject & {
  customerId?: string;
  customer?: Partial<Customer>;
  representativeId?: string;
  representative?: Partial<Representative>;
  customerRemotedCode: string;
  recordMode?: RecordMode;
};

export type DCTraderSite = BaseObject & {
  traderSiteId?: string;
  traderSite?: Partial<TraderSite>;
  representativeId?: string;
  representative?: Partial<Representative>;
  traderSiteRemotedCode: string;
  recordMode?: RecordMode;
};

export type DCValidationLog = BaseObject & {
  representativeId?: string;
  representativeName?: string;
  tableName: string;
  remotedCode: string;
  validationRuleDescription?: string;
  exported: boolean;
  lastExported?: string;
};

export type DCValidationLogsFiltersData = TableFilterData & {
  representativeIds?: string[];
  tableNames?: string[];
  exported?: boolean;
  dateFrom?: string;
  dateTo?: string;
};
